import { useEffect, useState } from "react";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import Button from "../../components/ui/button/Button";
import TableDropdown from "../../components/common/TableDropdown";
import { TableSkeleton } from "../../components/ui/skeleton";
import { Modal } from "../../components/ui/modal";
import { useModal } from "../../hooks/useModal";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import {
  fetchWorkspaces,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
  archiveWorkspace,
  restoreWorkspace,
  getWorkspaceMembers,
  addWorkspaceMember,
  removeWorkspaceMember,
  Workspace,
  WorkspaceMember,
  CreateWorkspaceRequest,
} from "../../lib/api/workspaces";
import { useToast } from "../../contexts/ToastContext";
import { fetchTenantUsers, TeamMember } from "../../lib/api/tenant";

export default function Workspaces() {
  const { success, error: showError } = useToast();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Modal states
  const createModal = useModal();
  const editModal = useModal();
  const deleteModal = useModal();

  // Form states
  const [formData, setFormData] = useState<CreateWorkspaceRequest>({
    name: "",
    operator_number: "",
    description: "",
  });
  const [editingWorkspace, setEditingWorkspace] = useState<Workspace | null>(null);
  const [deletingWorkspace, setDeletingWorkspace] = useState<Workspace | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Members state
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [newMemberUserId, setNewMemberUserId] = useState("");
  const [memberSubmitting, setMemberSubmitting] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<TeamMember[]>([]);

  // Load workspaces
  const loadWorkspaces = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchWorkspaces({ is_active: showArchived ? false : true });
      setWorkspaces(response.results);
    } catch (err) {
      console.error("Error fetching workspaces:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load workspaces. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkspaces();
  }, [showArchived]);

  // Handle create workspace
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await createWorkspace(formData);
      success('Workspace Created', 'New workspace has been created.');
      createModal.closeModal();
      setFormData({ name: "", operator_number: "", description: "" });
      loadWorkspaces();
    } catch (err) {
      console.error("Error creating workspace:", err);
      showError('Operation Failed', err instanceof Error ? err.message : 'Could not create workspace.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit workspace
  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingWorkspace) return;

    try {
      setSubmitting(true);
      await updateWorkspace(editingWorkspace.id, formData);
      success('Workspace Updated', 'The workspace has been updated.');
      editModal.closeModal();
      setEditingWorkspace(null);
      setFormData({ name: "", operator_number: "", description: "" });
      loadWorkspaces();
    } catch (err) {
      console.error("Error updating workspace:", err);
      showError('Operation Failed', err instanceof Error ? err.message : 'Could not update workspace.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete workspace
  const handleDelete = async () => {
    if (!deletingWorkspace) return;

    try {
      setSubmitting(true);
      await deleteWorkspace(deletingWorkspace.id);
      success('Workspace Deleted', 'The workspace has been removed.');
      deleteModal.closeModal();
      setDeletingWorkspace(null);
      loadWorkspaces();
    } catch (err) {
      console.error("Error deleting workspace:", err);
      showError('Operation Failed', err instanceof Error ? err.message : 'Could not delete workspace.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle archive/restore
  const handleArchiveToggle = async (workspace: Workspace) => {
    try {
      if (workspace.is_active) {
        await archiveWorkspace(workspace.id);
        success('Workspace Archived', 'The workspace has been archived.');
      } else {
        await restoreWorkspace(workspace.id);
        success('Workspace Restored', 'The workspace has been restored.');
      }
      loadWorkspaces();
    } catch (err) {
      console.error("Error toggling workspace archive status:", err);
      showError('Operation Failed', err instanceof Error ? err.message : 'Could not update workspace status.');
    }
  };

  // Load members for the currently-editing workspace
  const loadMembers = async (workspaceId: string) => {
    try {
      setMembersLoading(true);
      const data = await getWorkspaceMembers(workspaceId);
      setMembers(data);
    } catch (err) {
      console.error("Error fetching workspace members:", err);
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  // Handle adding a member
  const handleAddMember = async () => {
    if (!editingWorkspace || !newMemberUserId.trim()) return;
    try {
      setMemberSubmitting(true);
      await addWorkspaceMember(editingWorkspace.id, newMemberUserId.trim());
      setNewMemberUserId("");
      await loadMembers(editingWorkspace.id);
      success("Member Added", "The user has been added to the workspace.");
    } catch (err) {
      console.error("Error adding workspace member:", err);
      showError("Operation Failed", err instanceof Error ? err.message : "Could not add member.");
    } finally {
      setMemberSubmitting(false);
    }
  };

  // Handle removing a member
  const handleRemoveMember = async (userPk: string) => {
    if (!editingWorkspace) return;
    try {
      setMemberSubmitting(true);
      await removeWorkspaceMember(editingWorkspace.id, userPk);
      await loadMembers(editingWorkspace.id);
      success("Member Removed", "The user has been removed from the workspace.");
    } catch (err) {
      console.error("Error removing workspace member:", err);
      showError("Operation Failed", err instanceof Error ? err.message : "Could not remove member.");
    } finally {
      setMemberSubmitting(false);
    }
  };

  // Open edit modal with workspace data
  const openEditModal = (workspace: Workspace) => {
    setEditingWorkspace(workspace);
    setFormData({
      name: workspace.name,
      operator_number: workspace.operator_number || "",
      description: workspace.description || "",
    });
    setMembers([]);
    setNewMemberUserId("");
    editModal.openModal();
    loadMembers(workspace.id);
    fetchTenantUsers()
      .then((res) => setTenantUsers(res.users.filter((u) => u.is_active)))
      .catch(() => setTenantUsers([]));
  };

  // Open delete modal
  const openDeleteModal = (workspace: Workspace) => {
    setDeletingWorkspace(workspace);
    deleteModal.openModal();
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <PageMeta
        title="Workspaces | RegulAgent"
        description="Manage workspaces for multi-client operations"
      />

      <PageBreadcrumb pageTitle="Workspaces" />

      <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <div className="px-6 py-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Workspaces
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Total: {workspaces.length} workspaces
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowArchived(!showArchived)}
                className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-brand-500 dark:hover:text-brand-400"
              >
                {showArchived ? "Hide Archived" : "Show Archived"}
              </button>
              <Button size="sm" onClick={createModal.openModal}>
                Create Workspace
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-6 pb-4">
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="overflow-hidden">
            <div className="max-w-full overflow-x-auto">
              <TableSkeleton rows={5} columns={6} />
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && workspaces.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400">
                {showArchived
                  ? "No archived workspaces found"
                  : "No workspaces found. Create your first workspace to get started."}
              </p>
            </div>
          </div>
        )}

        {/* Workspaces Table */}
        {!loading && workspaces.length > 0 && (
          <div className="overflow-hidden">
            <div className="max-w-full overflow-x-auto">
              <Table>
                <TableHeader className="border-gray-100 border-y dark:border-white/[0.05]">
                  <TableRow>
                    <TableCell
                      isHeader
                      className="py-3 px-6 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                    >
                      Name
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                    >
                      Operator Number
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                    >
                      Filings
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                    >
                      Status
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                    >
                      Created
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                    >
                      <div className="relative">
                        <span className="sr-only">Action</span>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                  {workspaces.map((workspace) => (
                    <TableRow key={workspace.id}>
                      <TableCell className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-800 text-theme-sm dark:text-gray-300">
                            {workspace.name}
                          </span>
                          {workspace.description && (
                            <span className="text-xs text-gray-500 dark:text-gray-500">
                              {workspace.description}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-gray-700 text-theme-sm dark:text-gray-400">
                        {workspace.operator_number || "—"}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-gray-700 text-theme-sm dark:text-gray-400">
                        {workspace.filing_count}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            workspace.is_active
                              ? "bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-300"
                              : "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300"
                          }`}
                        >
                          {workspace.is_active ? "Active" : "Archived"}
                        </span>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-gray-700 whitespace-nowrap text-theme-sm dark:text-gray-400">
                        {formatDate(workspace.created_at)}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-gray-700 text-theme-sm dark:text-gray-400">
                        <div className="relative inline-block">
                          <TableDropdown
                            dropdownButton={
                              <button className="text-gray-500 dark:text-gray-400">
                                <svg
                                  className="fill-current"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M5.99902 10.245C6.96552 10.245 7.74902 11.0285 7.74902 11.995V12.005C7.74902 12.9715 6.96552 13.755 5.99902 13.755C5.03253 13.755 4.24902 12.9715 4.24902 12.005V11.995C4.24902 11.0285 5.03253 10.245 5.99902 10.245ZM17.999 10.245C18.9655 10.245 19.749 11.0285 19.749 11.995V12.005C19.749 12.9715 18.9655 13.755 17.999 13.755C17.0325 13.755 16.249 12.9715 16.249 12.005V11.995C16.249 11.0285 17.0325 10.245 17.999 10.245ZM13.749 11.995C13.749 11.0285 12.9655 10.245 11.999 10.245C11.0325 10.245 10.249 11.0285 10.249 11.995V12.005C10.249 12.9715 11.0325 13.755 11.999 13.755C12.9655 13.755 13.749 12.9715 13.749 12.005V11.995Z"
                                    fill=""
                                  />
                                </svg>
                              </button>
                            }
                            dropdownContent={
                              <>
                                <button
                                  onClick={() => openEditModal(workspace)}
                                  className="text-xs flex w-full rounded-lg px-3 py-2 text-left font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleArchiveToggle(workspace)}
                                  className="text-xs flex w-full rounded-lg px-3 py-2 text-left font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
                                >
                                  {workspace.is_active ? "Archive" : "Restore"}
                                </button>
                                <button
                                  onClick={() => openDeleteModal(workspace)}
                                  className="text-xs flex w-full rounded-lg px-3 py-2 text-left font-medium text-error-500 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-500/10 dark:hover:text-error-300"
                                >
                                  Delete
                                </button>
                              </>
                            }
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Create Workspace Modal */}
      <Modal
        isOpen={createModal.isOpen}
        onClose={createModal.closeModal}
        className="max-w-[600px] p-5 lg:p-10"
      >
        <form onSubmit={handleCreate}>
          <h4 className="mb-6 text-lg font-medium text-gray-800 dark:text-white/90">
            Create Workspace
          </h4>

          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                type="text"
                placeholder="Enter workspace name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </div>

            <div>
              <Label>Operator Number</Label>
              <Input
                type="text"
                placeholder="Enter operator number"
                value={formData.operator_number}
                onChange={(e) =>
                  setFormData({ ...formData, operator_number: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Description</Label>
              <Input
                type="text"
                placeholder="Enter description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>
          </div>

          <div className="flex items-center justify-end w-full gap-3 mt-6">
            <Button
              size="sm"
              variant="outline"
              onClick={createModal.closeModal}
              type="button"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button size="sm" type="submit" disabled={submitting}>
              {submitting ? "Creating..." : "Create Workspace"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Workspace Modal */}
      <Modal
        isOpen={editModal.isOpen}
        onClose={editModal.closeModal}
        className="max-w-[600px] p-5 lg:p-10"
      >
        <form onSubmit={handleEdit}>
          <h4 className="mb-6 text-lg font-medium text-gray-800 dark:text-white/90">
            Edit Workspace
          </h4>

          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                type="text"
                placeholder="Enter workspace name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </div>

            <div>
              <Label>Operator Number</Label>
              <Input
                type="text"
                placeholder="Enter operator number"
                value={formData.operator_number}
                onChange={(e) =>
                  setFormData({ ...formData, operator_number: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Description</Label>
              <Input
                type="text"
                placeholder="Enter description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>
          </div>

          {/* Members section */}
          <div className="mt-6">
            <h5 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
              Members
            </h5>

            {membersLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading members...</p>
            ) : members.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No members yet.</p>
            ) : (
              <ul className="mb-3 divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-white/[0.05] dark:border-white/[0.08]">
                {members.map((member) => (
                  <li
                    key={member.id}
                    className="flex items-center justify-between px-3 py-2"
                  >
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {member.user_name}{" "}
                      <span className="text-gray-500 dark:text-gray-500">
                        ({member.user_email})
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(member.user)}
                      disabled={memberSubmitting}
                      className="text-xs font-medium text-error-500 hover:text-error-700 disabled:opacity-50 dark:text-error-400 dark:hover:text-error-300"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Add member */}
            <div className="flex items-center gap-2">
              <select
                value={newMemberUserId}
                onChange={(e) => setNewMemberUserId(e.target.value)}
                className="flex-1 rounded-lg border border-gray-300 dark:border-white/[0.1] bg-white dark:bg-white/[0.03] px-3 py-2 text-sm text-gray-800 dark:text-white/90 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
                disabled={memberSubmitting}
              >
                <option value="">Select a team member…</option>
                {tenantUsers
                  .filter((u) => !members.some((m) => m.user_email === u.email))
                  .map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {u.email}{u.first_name || u.last_name ? ` — ${[u.first_name, u.last_name].filter(Boolean).join(" ")}` : ""}
                    </option>
                  ))}
              </select>
              <Button
                size="sm"
                type="button"
                onClick={handleAddMember}
                disabled={memberSubmitting || !newMemberUserId}
              >
                {memberSubmitting ? "Adding..." : "Add"}
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-end w-full gap-3 mt-6">
            <Button
              size="sm"
              variant="outline"
              onClick={editModal.closeModal}
              type="button"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button size="sm" type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.closeModal}
        className="max-w-[500px] p-5 lg:p-10"
      >
        <div className="text-center">
          <div className="relative flex items-center justify-center z-1 mb-7">
            <svg
              className="fill-error-50 dark:fill-error-500/15"
              width="90"
              height="90"
              viewBox="0 0 90 90"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M34.364 6.85053C38.6205 -2.28351 51.3795 -2.28351 55.636 6.85053C58.0129 11.951 63.5594 14.6722 68.9556 13.3853C78.6192 11.0807 86.5743 21.2433 82.2185 30.3287C79.7862 35.402 81.1561 41.5165 85.5082 45.0122C93.3019 51.2725 90.4628 63.9451 80.7747 66.1403C75.3648 67.3661 71.5265 72.2695 71.5572 77.9156C71.6123 88.0265 60.1169 93.6664 52.3918 87.3184C48.0781 83.7737 41.9219 83.7737 37.6082 87.3184C29.8831 93.6664 18.3877 88.0266 18.4428 77.9156C18.4735 72.2695 14.6352 67.3661 9.22531 66.1403C-0.462787 63.9451 -3.30193 51.2725 4.49185 45.0122C8.84391 41.5165 10.2138 35.402 7.78151 30.3287C3.42572 21.2433 11.3808 11.0807 21.0444 13.3853C26.4406 14.6722 31.9871 11.951 34.364 6.85053Z"
                fill=""
                fillOpacity=""
              />
            </svg>

            <span className="absolute -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2">
              <svg
                className="fill-error-600 dark:fill-error-500"
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.62684 11.7496C9.04105 11.1638 9.04105 10.2141 9.62684 9.6283C10.2126 9.04252 11.1624 9.04252 11.7482 9.6283L18.9985 16.8786L26.2485 9.62851C26.8343 9.04273 27.7841 9.04273 28.3699 9.62851C28.9556 10.2143 28.9556 11.164 28.3699 11.7498L21.1198 18.9999L28.3699 26.25C28.9556 26.8358 28.9556 27.7855 28.3699 28.3713C27.7841 28.9571 26.8343 28.9571 26.2485 28.3713L18.9985 21.1212L11.7482 28.3715C11.1624 28.9573 10.2126 28.9573 9.62684 28.3715C9.04105 27.7857 9.04105 26.836 9.62684 26.2502L16.8771 18.9999L9.62684 11.7496Z"
                  fill=""
                />
              </svg>
            </span>
          </div>

          <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90 sm:text-title-sm">
            Delete Workspace?
          </h4>
          <p className="text-sm leading-6 text-gray-500 dark:text-gray-400">
            Are you sure you want to delete "{deletingWorkspace?.name}"? This
            action cannot be undone.
          </p>

          <div className="flex items-center justify-center w-full gap-3 mt-7">
            <Button
              size="sm"
              variant="outline"
              onClick={deleteModal.closeModal}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleDelete}
              disabled={submitting}
              className="bg-error-500 hover:bg-error-600"
            >
              {submitting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
